import React from "react";
import { Box } from "@mui/material";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";

import VideoItem from "../../../VideoItem";
import { Video } from "../../../../api/types";

interface CustomPlaylistListProps {
  videos: Video[];
  onReorder: (oldIndex: number, newIndex: number) => void;
  onRemove: (videoToken: string) => void;
  showRemoveButton?: boolean;
  style?: React.CSSProperties;
}

const SortableVideoItem = ({
  video,
  index,
  onRemove,
  showRemoveButton = true,
}: {
  video: Video;
  index: number;
  onRemove: (videoToken: string) => void;
  showRemoveButton?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: video.token,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <VideoItem
      video={video}
      index={index}
      showRemoveButton={showRemoveButton}
      showDragHandle
      onRemove={onRemove}
      dragProps={{
        attributes,
        listeners,
        setNodeRef,
        style,
      }}
    />
  );
};

const CustomPlaylistList: React.FC<CustomPlaylistListProps> = ({
  videos,
  onReorder,
  onRemove,
  showRemoveButton = true,
  style,
}) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = videos.findIndex((v) => v.token === active.id);
      const newIndex = videos.findIndex((v) => v.token === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  return (
    <Box sx={{ paddingTop: "20px" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          fontWeight: 500,
          fontSize: "12px",
        }}
      >
        <Box sx={{ width: 50, padding: "1em 0.6em", textAlign: "center" }} />
        <Box sx={{ width: 100, padding: "1em 0.6em", textAlign: "center" }}>
          Thumbnail
        </Box>
        <Box sx={{ flex: 1, padding: "1em 0.6em", paddingLeft: 2 }}>
          Video Title
        </Box>
        <Box sx={{ width: 50, padding: "1em 0.6em", textAlign: "center" }} />
      </Box>

      {/* Video List */}
      <Box
        sx={{
          maxHeight: "400px",
          overflowY: "auto",
          overflowX: "hidden",
          ...style,
        }}
      >
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
        >
          <SortableContext
            items={videos.map((v) => v.token)}
            strategy={verticalListSortingStrategy}
          >
            {videos.map((video, index) => (
              <SortableVideoItem
                key={video.token}
                video={video}
                index={index}
                onRemove={onRemove}
                showRemoveButton={showRemoveButton}
              />
            ))}
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};

export default CustomPlaylistList;
